/* eslint-disable @typescript-eslint/non-nullable-type-assertion-style */
import React, { useState, useEffect } from 'react';
import { Input, Button, Spin, DatePicker, Select, Table } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { type PledgePayment, type PledgeResponse } from '../store/pledgeTypes';
import { closePledgeAction } from '../store/pledgeActions';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import type { DatePickerProps } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';

// const { TextArea } = Input;

interface Props {
  pledge: PledgeResponse;
  closeModal: () => void;
}

export default function PaymentModal({
  pledge,
  closeModal,
}: Props): JSX.Element {
  const [remainingPrincipal, setRemainingPrincipal] = useState<number>(0);
  const [pendingInterest, setPendingInterest] = useState<number>(0);
  const [offlinePrincipal, setOfflinePrincipal] = useState<number>(0);
  const [offlineInterest, setOfflineInterest] = useState<number>(0);
  const [onlinePrincipal, setOnlinePrincipal] = useState<number>(0);
  const [onlineInterest, setOnlineInterest] = useState<number>(0);
  // const [amountPaid, setAmountPaid] = useState<string>('');
  // const [cashier, setCashier] = useState<string>('');
  // const [notes, setNotes] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  // const [paymentType, setPaymentType] = useState<string>('');
  // const [paymentMethod, setPaymentMethod] = useState<string>('');
  const [datePickerDate, setDatePickerDate] = useState<Dayjs | null>(dayjs());
  const [transactionDateString, setTransactionDateString] = useState<string>(
    dayjs().toISOString()
  );
  // const [cashier, setCashier] = useState<string>('');
  // const [otherTransactionAmount, setOtherTransactionAmount] =
  //   useState<string>('');
  // const [otherTransactionDescription, setOtherTransactionDescription] =
  //   useState<string>('');

  const onChangeDate: DatePickerProps['onChange'] = (
    date: Dayjs | null,
    dateString: string
  ): void => {
    setDatePickerDate(date);
    setTransactionDateString(dateString);
  };

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // const [extra, setExtra] = useState<number>(0);
  const [reducingField, setReducingField] = useState<number>(0);
  const [closureType, setClosureType] = useState<any>({});
  const [documentChargeOnClosure, setDocumentChargeOnClosure] =
    useState<number>(0);
  const [onlineDocumentCharge, setOnlineDocumentCharge] = useState<number>(0);
  const [offlineDocumentCharge, setOfflineDocumentCharge] = useState<number>(0);

  const handleClosureType = (jewelId: string, value: string) => {
    setClosureType((prevState: any) => ({
      ...prevState,
      [jewelId]: value,
    }));
  };

  const jewelColumns = [
    {
      title: 'Loan Number/Sale Number',
      dataIndex: 'loanNumber',
      key: 'loanNumber',
      render: (_: any, record: any, index: number) => (
        <p>
          {record.loanNumber !== '' ? record.loanNumber : record.salesNumber}
        </p>
      ),
    },
    {
      title: 'Item Type',
      dataIndex: 'itemType',
      key: 'itemType',
      render: (_: any, record: any) => <p>{record.itemType}</p>,
    },
    {
      title: 'Closure Type',
      dataIndex: 'closureType',
      key: 'closureType',
      render: (_: any, record: any, index: number) => {
        console.log('redcord', record);
        return (
          <Select
            placeholder="Select Closure Type"
            options={[
              { value: 'Action', label: 'Action' },
              { value: 'Sales', label: 'Sales' },
              {
                value: 'Customer',
                label: 'Customer',
              },
              {
                value: 'Repledge',
                label: 'Repledge',
              },
            ]}
            onChange={(value: string): void => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              handleClosureType(record._id, value);
            }}
            className="payment__select"
          />
        );
      },
    },
  ];

  const dispatch = useDispatch<AppDispatch>();

  const makePledgePayment = async () => {
    setIsLoading(true);

    const totalInterest =
      Number(onlineInterest) + Number(offlineInterest) + Number(reducingField);
    const totalPrincipal = Number(onlinePrincipal) + Number(offlinePrincipal);
    const isPayingFullInterest = totalInterest >= Number(pendingInterest);
    const isPayingFullPrincipal = totalPrincipal === Number(remainingPrincipal);
    const isFullPayment = isPayingFullInterest && isPayingFullPrincipal;

    const payload: PledgePayment = {
      pledgeNumber: pledge.pledgeNumber,
      transactionDate: transactionDateString,
      description,
      interestAmount: Number(onlineInterest) + Number(offlineInterest),
      principal: totalPrincipal,
      onlinePrincipal,
      offlinePrincipal,
      onlineInterest,
      offlineInterest,
      branch: pledge.branch._id,
      extra:
        totalInterest > Number(pendingInterest)
          ? totalInterest - Number(pledge.pendingInterest)
          : 0,
      reducingField,
    };

    // Add closureType only if paying fully
    if (isFullPayment) {
      payload.closureType = closureType;
      payload.documentCharge = documentChargeOnClosure;
      payload.onlineDocumentCharge = onlineDocumentCharge;
      payload.offlineDocumentCharge = offlineDocumentCharge;
    }

    // Add paymentType based on interest payment
    if (!isPayingFullInterest) {
      payload.paymentType = 'part';
    } else {
      payload.paymentType = 'full';
    }

    await closePledgeAction(dispatch, payload);
    setIsLoading(false);
    closeModal();
  };

  useEffect(() => {
    setRemainingPrincipal(pledge.remainingPrincipal);
    setPendingInterest(pledge.pendingInterest);
  }, []);

  const isFullPayment =
    Number(onlinePrincipal) + Number(offlinePrincipal) === Number(remainingPrincipal) &&
    Number(onlineInterest) + Number(offlineInterest) + Number(reducingField) >= Number(pendingInterest) &&
    Number(remainingPrincipal) > 0;

  const allClosureTypesSelected =
    !isFullPayment ||
    (pledge?.jewels ?? []).every((j: any) => Boolean(closureType[j._id]));

  // charges can be fractional, so allow sub-paisa float error
  const isDocumentChargeSplitValid =
    !isFullPayment ||
    pledge.documentChargeHandling !== 'collectOnClose' ||
    Math.abs(
      Number(onlineDocumentCharge) +
        Number(offlineDocumentCharge) -
        Number(documentChargeOnClosure)
    ) <= 0.009;

  return (
    <div className="pledge__payment__modal">
      <div className="divider" />
      <div className="payment__form">
        <div>
          <h4>Principal to be paid</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            disabled
            value={remainingPrincipal}
            // onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            //   setOnlinePrincipal(Number(e.target.value));
            // }}
            className="payment__input"
            placeholder="Enter principal"
          />
        </div>
        {/* <div>
          <h4>Offline Principal</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={offlinePrincipal}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOfflinePrincipal(e.target.value);
            }}
            className="payment__input"
            placeholder="Enter offline principal"
          />
        </div> */}
        <div>
          <h4>Pending Interest ({pledge.daysDifference} days)</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            disabled
            value={pendingInterest}
            // onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            //   setOnlineInterest(Number(e.target.value));
            // }}
            className="payment__input"
            placeholder="Enter interest"
          />
        </div>
        <div>
          <h4>Online Interest</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={onlineInterest}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOnlineInterest(Number(e.target.value));
            }}
            className="payment__input"
            placeholder="Enter interest"
          />
          {Number(onlineInterest) +
            Number(offlineInterest) +
            Number(reducingField) >
            Number(pledge?.pendingInterest) && (
            <p>
              Receiving an extra amount of{' '}
              {Number(onlineInterest) +
                Number(offlineInterest) +
                Number(reducingField) -
                Number(pledge?.pendingInterest)}{' '}
              rupees
            </p>
          )}
        </div>
        <div>
          <h4>Offline Interest</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={offlineInterest}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOfflineInterest(Number(e.target.value));
            }}
            className="payment__input"
            placeholder="Enter interest"
          />
          {Number(onlineInterest) +
            Number(offlineInterest) +
            Number(reducingField) >
            Number(pledge?.pendingInterest) && (
            <p>
              Receiving an extra amount of{' '}
              {Number(onlineInterest) +
                Number(offlineInterest) +
                Number(reducingField) -
                Number(pledge?.pendingInterest)}{' '}
              rupees
            </p>
          )}
        </div>
        <div>
          <h4>Reducing Field</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={reducingField}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setReducingField(Number(e.target.value));
            }}
            className="payment__input"
            placeholder="Enter Reducing Field"
          />
        </div>
        <div></div>
        {Number(onlineInterest) +
          Number(offlineInterest) +
          Number(reducingField) >=
          Number(pendingInterest) && (
          <>
            <div>
              <h4>Online Principal</h4>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                value={onlinePrincipal}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setOnlinePrincipal(Number(e.target.value));
                }}
                className="payment__input"
                placeholder="Enter online principal"
              />
            </div>
            <div>
              <h4>Offline Principal</h4>
              <Input
                type="number"
                onWheel={(e) => {
                  e.currentTarget.blur();
                }}
                value={offlinePrincipal}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setOfflinePrincipal(Number(e.target.value));
                }}
                className="payment__input"
                placeholder="Enter offline principal"
              />
            </div>
          </>
        )}
        {/* <div>
          <h4>Extra</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={extra}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setExtra(Number(e.target.value));
            }}
            className="payment__input"
            placeholder="Enter Extra"
          />
        </div> */}
        {/* <div>
          <h4>Offline Interest</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={offlineInterest}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOfflineInterest(e.target.value);
            }}
            className="payment__input"
            placeholder="Enter offline interest"
          />
        </div> */}
        {/* <div>
          <h4>Cashier</h4>
          <Input
            value={cashier}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCashier(e.target.value);
            }}
            className="payment__input"
            placeholder="Enter cashier"
          />
        </div> */}
        <div>
          <h4>Transaction Date</h4>
          <DatePicker
            value={datePickerDate}
            // disabledDate={disabledDate}
            className="payment__input"
            onChange={onChangeDate}
          />
        </div>
        {/* <div>
          <h4>Closure Type</h4>
          <Select
            defaultValue={closureType}
            placeholder="Select Closure Type"
            options={[
              { value: 'Action', label: 'Action' },
              { value: 'Sales', label: 'Sales' },
              {
                value: 'Customer',
                label: 'Customer',
              },
              {
                value: 'Repledge',
                label: 'Repledge',
              },
            ]}
            onChange={(value: string): void => {
              setClosureType(value);
            }}
            className="payment__select"
          />
        </div> */}
        {Number(onlinePrincipal) + Number(offlinePrincipal) ===
          Number(remainingPrincipal) &&
          Number(onlineInterest) +
            Number(offlineInterest) +
            Number(reducingField) >=
            Number(pendingInterest) && (
            <>
              <div style={{ width: '100%' }}>
                <h2>Jewels</h2>
                <Table
                  dataSource={pledge?.jewels}
                  columns={jewelColumns}
                  pagination={false}
                ></Table>
              </div>
              {pledge.documentChargeHandling === 'collectOnClose' && (
                <>
                  <div>
                    <h4>Document Charge</h4>
                    <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                      }}
                      value={documentChargeOnClosure}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        // a changed total invalidates the split, so fall back to fully offline
                        const value = Number(e.target.value);
                        setDocumentChargeOnClosure(value);
                        setOnlineDocumentCharge(0);
                        setOfflineDocumentCharge(value);
                      }}
                      className="payment__input"
                      placeholder="Enter document charge"
                    />
                  </div>
                  <div>
                    <h4>Online Document Charge</h4>
                    <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                      }}
                      value={onlineDocumentCharge}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setOnlineDocumentCharge(Number(e.target.value));
                      }}
                      className="payment__input"
                      placeholder="Enter online document charge"
                    />
                  </div>
                  <div>
                    <h4>Offline Document Charge</h4>
                    <Input
                      type="number"
                      onWheel={(e) => {
                        e.currentTarget.blur();
                      }}
                      value={offlineDocumentCharge}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setOfflineDocumentCharge(Number(e.target.value));
                      }}
                      className="payment__input"
                      placeholder="Enter offline document charge"
                    />
                  </div>
                  {!isDocumentChargeSplitValid && (
                    <p className="error__message">
                      Online + offline must equal the document charge
                    </p>
                  )}
                </>
              )}
            </>
          )}
        {/* <div>
          <h4>Cashier</h4>
          <Input
            value={cashier}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setCashier(e.target.value);
            }}
            className="payment__input"
            placeholder="Enter cashier"
          />
        </div> */}
        {/* <div>
          <h4>Amount Paid</h4>
          <Input
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            value={amountPaid}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setAmountPaid(e.target.value);
            }}
            className="payment__input"
            placeholder="Enter amount"
          />
        </div> */}
        {/* <div>
          <h4>Payment Method</h4>
          <Select
            style={{ width: 280, height: 56 }}
            placeholder="Select payment method"
            value={paymentMethod}
            onChange={(value: string): void => {
              setPaymentMethod(value);
            }}
            options={[
              { label: 'Cash', value: 'Cash' },
              { label: 'Card', value: 'Card' },
              { label: 'Online', value: 'Online' },
              { label: 'Other', value: 'Other' },
            ]}
            className="payment__select"
          />
        </div> */}
        {/* <div>
          <h4>Payment Type</h4>
          <Select
            style={{ width: 280, height: 56 }}
            placeholder="Select payment type"
            value={paymentType}
            onChange={(value: string): void => {
              setPaymentType(value);
            }}
            options={[
              { label: 'full', value: 'full' },
              { label: 'partial', value: 'partial' },
            ]}
            className="payment__select"
          />
        </div> */}
        <div>
          <h4>Description</h4>
          <Input
            value={description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setDescription(e.target.value);
            }}
            className="payment__longinput"
            placeholder="Enter description"
          />
        </div>
        {/* <div>
          <h4>Notes</h4>
          <TextArea
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setNotes(e.target.value);
            }}
            className="payment__textarea"
            placeholder="Enter notes"
          />
        </div> */}
        {/* <div>
          <h4>Other Transaction Amount</h4>
          <Input
            value={otherTransactionAmount}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setOtherTransactionAmount(e.target.value);
            }}
            type="number"
            onWheel={(e) => {
              e.currentTarget.blur();
            }}
            className="payment__longinput"
            placeholder="Enter other transaction amount"
          />
        </div>
        <div>
          <h4>Other Transaction Description</h4>
          <TextArea
            value={otherTransactionDescription}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
              setOtherTransactionDescription(e.target.value);
            }}
            className="payment__textarea"
            placeholder="Enter other transaction description"
          />
        </div> */}
      </div>
      <div className="payment__actions__section">
        {/* <Button
          onClick={() => {
            // setTemp(!temp);
          }}
          className="payment__cancel__button"
        >
          Cancel
        </Button> */}
        <Button
          onClick={makePledgePayment}
          disabled={
            Number(onlineInterest) + Number(offlineInterest) + Number(reducingField) <= 0 ||
            !allClosureTypesSelected ||
            !isDocumentChargeSplitValid
          }
          className="payment__save__button"
        >
          {isLoading ? (
            <Spin
              indicator={
                <LoadingOutlined
                  style={{ fontSize: 24, color: 'white' }}
                  spin
                />
              }
            />
          ) : (
            <span>Save</span>
          )}
        </Button>
      </div>
    </div>
  );
}
